'use client';

import { ArrowLeft, AlertTriangle, CheckCircle, Shield, FileText, Scale, Gavel, Sparkles } from 'lucide-react';
import { AnalysisResult } from '@/lib/analysis-schema';
import { Link } from 'react-router-dom';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';

interface AnalysisResultsProps {
  results: AnalysisResult;
  onReset: () => void;
}

export default function AnalysisResults({ results, onReset }: AnalysisResultsProps) {
  const getSeverityColor = (severity: string) => {
    if (severity === 'High') return 'border-destructive bg-destructive/10';
    if (severity === 'Medium') return 'border-accent bg-accent/10';
    return 'border-primary bg-primary/10';
  };

  const getSeverityBadgeColor = (severity: string) => {
    if (severity === 'High') return 'bg-destructive/20 text-destructive border border-destructive/30';
    if (severity === 'Medium') return 'bg-accent/20 text-accent border border-accent/30';
    return 'bg-primary/20 text-primary border border-primary/30';
  };

  const getSeverityIcon = (severity: string) => {
    if (severity === 'High') return <AlertTriangle className="w-5 h-5 text-destructive" />;
    if (severity === 'Medium') return <AlertTriangle className="w-5 h-5 text-accent" />;
    return <AlertTriangle className="w-5 h-5 text-primary" />;
  };

  const getDamagesPotentialColor = (potential: string) => {
    if (potential === 'Significant') return 'text-success';
    if (potential === 'Moderate') return 'text-accent';
    return 'text-muted-foreground';
  };

  const totalViolations = results?.legalSummary?.totalViolations ?? results?.fcraViolations?.length ?? 0;
  const highSeverityCount = results?.fcraViolations?.filter(v => v?.severity === 'High')?.length ?? 0;
  const mediumSeverityCount = results?.fcraViolations?.filter(v => v?.severity === 'Medium')?.length ?? 0;

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[40%] h-[40%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[10%] w-[30%] h-[30%]" />
        <div className="absolute inset-0 radar-grid opacity-10" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={creditReportMoneyLogo}
              alt="Credit Report Money"
              className="h-10 w-auto"
            />
          </Link>
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 rounded-xl glass-panel border border-border/50 hover:border-primary/50 transition-all text-primary font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Analyze New Report</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="mb-8 animate-reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel border border-primary/20 text-primary mb-4">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Analysis Complete</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            <span className="gradient-text-gold">FCRA Violation Analysis</span>
          </h1>
          <p className="text-lg text-muted-foreground">Comprehensive legal exposure assessment</p>
        </div>

        {/* Legal Exposure Summary */}
        <div className={`rounded-2xl p-8 mb-8 border-2 animate-reveal ${
          totalViolations > 0 
            ? 'bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30' 
            : 'bg-gradient-to-br from-success/20 to-success/5 border-success/30'
        }`} style={{ animationDelay: '100ms' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              totalViolations > 0 ? 'bg-primary/20' : 'bg-success/20'
            }`}>
              <Scale className={`w-7 h-7 ${totalViolations > 0 ? 'text-primary' : 'text-success'}`} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Legal Exposure Summary</h2>
              <p className="text-muted-foreground mt-1">
                {totalViolations > 0 
                  ? 'FCRA/FDCPA Violations Detected - Review Required' 
                  : 'No Violations Detected'}
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Violations', value: totalViolations },
              { label: 'High Severity', value: highSeverityCount },
              { label: 'Medium Severity', value: mediumSeverityCount },
              { label: 'Damages Potential', value: results?.legalSummary?.estimatedDamagesPotential ?? 'Low', isText: true },
            ].map((stat, idx) => (
              <div key={idx} className="glass-panel p-4 rounded-xl border border-border/50">
                <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                <p className={`text-3xl font-bold ${stat.isText ? getDamagesPotentialColor(stat.value as string) : 'text-foreground'}`}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
          {results?.legalSummary?.attorneyReferralRecommended && (
            <div className="glass-panel p-6 rounded-xl border-2 border-accent/30 bg-accent/10">
              <div className="flex items-center gap-3 mb-2">
                <Gavel className="w-6 h-6 text-accent" />
                <h3 className="font-bold text-xl text-foreground">Attorney Referral Recommended</h3>
              </div>
              <p className="text-muted-foreground">Based on the violations found, consulting with a consumer rights attorney is strongly recommended to evaluate potential legal remedies.</p>
            </div>
          )}
        </div>

        {/* Report Summary */}
        {results?.reportSummary && (
          <div className="glass-panel rounded-2xl border border-border/50 p-8 mb-8 animate-reveal" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Report Summary</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { label: 'Consumer Name', value: results.reportSummary.consumerName ?? 'Not specified' },
                { label: 'Report Date', value: results.reportSummary.reportDate ?? 'Not specified' },
                { label: 'Accounts Analyzed', value: results.reportSummary.totalAccountsAnalyzed ?? 0 },
                { label: 'Source Bureau(s)', value: results.reportSummary.fileSource ?? 'Unknown' },
              ].map((item, idx) => (
                <div key={idx} className="p-4 bg-card/50 rounded-xl border border-border/50">
                  <p className="text-sm text-muted-foreground mb-1">{item.label}</p>
                  <p className="text-lg font-semibold text-foreground">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FCRA Violations */}
        {((results?.fcraViolations ?? [])?.length ?? 0) > 0 && (
          <div className="glass-panel rounded-2xl border-2 border-primary/30 p-8 mb-8 animate-reveal" style={{ animationDelay: '300ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">FCRA Violations Detected</h2>
            </div>
            <div className="bg-primary/5 border-l-4 border-primary p-4 mb-6 rounded-r-xl">
              <p className="text-sm font-medium text-foreground">
                <strong>Legal Basis:</strong> Fair Credit Reporting Act (FCRA) 15 USC Section 1681 and Fair Debt Collection Practices Act (FDCPA) 15 USC Section 1692
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                The violations below may entitle you to statutory damages, actual damages, and attorney fees.
              </p>
            </div>
            <div className="space-y-6">
              {(results?.fcraViolations ?? [])?.map?.((violation, idx) => (
                <div key={idx} className={`border-l-4 p-6 rounded-r-xl ${getSeverityColor(violation?.severity ?? 'Low')}`}>
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(violation?.severity ?? 'Low')}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityBadgeColor(violation?.severity ?? 'Low')}`}>
                          {violation?.severity ?? 'Low'} Severity
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/20 text-primary border border-primary/30">
                          {violation?.violationTitle ?? 'Unknown Violation'}
                        </span>
                      </div>

                      {violation?.accountsInvolved && (violation?.accountsInvolved ?? [])?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold mb-2 text-foreground">Accounts Involved:</p>
                          <div className="flex flex-wrap gap-2">
                            {(violation?.accountsInvolved ?? [])?.map?.((account, aIdx) => (
                              <span 
                                key={aIdx}
                                className="px-3 py-1 rounded-full text-xs font-medium bg-muted/50 text-foreground border border-border/50"
                              >
                                {account}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="bg-card/50 p-4 rounded-xl border border-border/50 mb-4">
                        <p className="text-sm font-semibold mb-1 text-foreground">Legal Basis:</p>
                        <p className="text-sm text-muted-foreground font-mono">{violation?.legalBasis ?? 'Not specified'}</p>
                      </div>

                      <div className="bg-card/50 p-4 rounded-xl border border-border/50 mb-4">
                        <p className="text-sm font-semibold mb-1 text-foreground">Violation Explanation:</p>
                        <p className="text-sm text-muted-foreground">{violation?.explanation ?? 'No explanation provided'}</p>
                      </div>

                      <div className="bg-success/10 p-4 rounded-xl border-2 border-success/30">
                        <p className="text-sm font-bold text-success mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Suggested Action:
                        </p>
                        <p className="text-sm text-foreground whitespace-pre-line">{violation?.suggestedAction ?? 'Contact a consumer rights attorney for guidance.'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Account Analysis */}
        {((results?.accountAnalysis ?? [])?.length ?? 0) > 0 && (
          <div className="glass-panel rounded-2xl border border-border/50 p-8 mb-8 animate-reveal" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Account Analysis</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Account Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Account #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Balance</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Comments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {(results?.accountAnalysis ?? [])?.slice?.(0, 50)?.map?.((account, idx) => (
                    <tr key={idx} className="hover:bg-card/50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{account?.accountName ?? 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{account?.accountNumber ?? 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          account?.status === 'Open' || account?.status === 'Current'
                            ? 'bg-success/20 text-success border border-success/30'
                            : account?.status === 'Closed'
                            ? 'bg-muted/50 text-muted-foreground border border-border/50'
                            : account?.status === 'Derogatory' || account?.status === 'Collection'
                            ? 'bg-destructive/20 text-destructive border border-destructive/30'
                            : 'bg-accent/20 text-accent border border-accent/30'
                        }`}>
                          {account?.status ?? 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{account?.balance ?? 'N/A'}</td>
                      <td className="px-4 py-3 text-sm max-w-md">
                        {account?.comments ? (
                          <div className="bg-accent/10 border-l-4 border-accent px-4 py-3 rounded-r-lg">
                            <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">{account.comments}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-8 border-2 border-primary/30 animate-reveal" style={{ animationDelay: '500ms' }}>
          <h2 className="text-2xl font-bold text-foreground mb-4">Overall Assessment</h2>
          <p className="text-lg leading-relaxed text-muted-foreground">{results?.summary ?? 'Analysis complete. Review the violations above for detailed findings and recommended actions.'}</p>
        </div>
      </div>
    </div>
  );
}
